import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAudioSync } from '../audio/useAudioSync';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { PromptPanel } from '../components/PromptPanel';
import { SaveIndicator } from '../components/SaveIndicator';
import { Alert, Button, Spinner } from '../components/ui';
import type { Project } from '../domain/schemas';
import { useObservable, useObservableSelector } from '../lib/useObservable';
import type { WorkspaceTab } from '../prompt/executor';
import { ProjectContext, type ProjectContextValue } from '../state/projectContext';
import { ProjectSession } from '../state/projectSession';
import { TABS, type Route } from '../state/route';
import { VisualHistoryContext } from '../state/historyContext';
import { VisualHistory } from '../scene/history';
import { useServices } from '../state/services';
import { describeBackup, downloadUnsavedCopy } from '../state/recovery';
import { toStorageError } from '../storage/errors';
import { acquireProjectLock } from '../state/projectLock';
import { AnimationWorkspace } from './AnimationWorkspace';
import { AssistantWorkspace } from './AssistantWorkspace';
import { AudioWorkspace } from './AudioWorkspace';
import { MusicWorkspace } from './MusicWorkspace';
import { SettingsWorkspace } from './SettingsWorkspace';
import { SyncWorkspace } from './SyncWorkspace';
import { VisualizerWorkspace } from './VisualizerWorkspace';

const TAB_LABELS: Record<WorkspaceTab, string> = {
  music: 'Music',
  animation: 'Animation',
  sync: 'Sync',
  visualizer: 'Visualizer',
  audio: 'Audio',
  assistant: 'Assistant',
  settings: 'Settings',
};

export function ProjectShell({
  projectId,
  tab,
  navigate,
}: {
  projectId: string;
  tab: WorkspaceTab;
  navigate: (route: Route) => void;
}) {
  const { repo, recovery } = useServices();
  const [session] = useState(() => new ProjectSession(repo, projectId, undefined, acquireProjectLock));
  const state = useObservable(session.state);

  useEffect(() => {
    void session.load();
    return () => {
      // Closing flushes pending edits. If that save fails, keep the edits and offer them
      // as a backup file on the Dashboard instead of dropping them.
      void session.dispose().then((saved) => {
        const { project, saveError } = session.snapshot;
        if (!saved && project) recovery.set({ project, error: saveError ?? 'The last save failed.' });
      });
    };
  }, [session, recovery]);

  // Warn before closing the tab with unsaved edits; flush when the page is hidden.
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (session.hasUnsavedChanges) {
        void session.saveNow();
        event.preventDefault();
        event.returnValue = ''; // older Safari and Chromium need this to show the prompt
      }
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') void session.saveNow();
    };
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void session.saveNow();
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('keydown', onKey);
    };
  }, [session]);

  if (state.status === 'loading' && !state.project) {
    return (
      <div className="p-6">
        <Spinner label="Opening project…" />
      </div>
    );
  }
  if (!state.project) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <Alert
          title={state.loadError?.message ?? 'Could not open the project.'}
          action={
            <div className="flex gap-2">
              {(state.loadError?.code === 'conflict' || state.loadError?.code === 'lock-failed') && (
                <Button size="sm" onClick={() => void session.load()} data-testid="project-retry-open">
                  Try again
                </Button>
              )}
              <Button size="sm" onClick={() => navigate({ name: 'dashboard' })}>Back to projects</Button>
            </div>
          }
        >
          {state.loadError?.details.map((d) => <p key={d} className="font-mono text-xs">{d}</p>)}
        </Alert>
      </div>
    );
  }
  return <OpenProject project={state.project} session={session} tab={tab} navigate={navigate} />;
}

function OpenProject({
  project,
  session,
  tab,
  navigate,
}: {
  project: Project;
  session: ProjectSession;
  tab: WorkspaceTab;
  navigate: (route: Route) => void;
}) {
  const { audio, selection, animation, motion, sync, syncSelection, analysis, repo } = useServices();
  const state = useObservable(session.state);
  const [backingUp, setBackingUp] = useState(false);
  const [backupNotice, setBackupNotice] = useState<{ tone: 'success' | 'warning' | 'error'; title: string; details: string[] } | null>(null);
  /** Downloads an importable backup of the unsaved edits; reports success only after the download. */
  const backup = async () => {
    setBackingUp(true);
    setBackupNotice(null);
    try {
      setBackupNotice(describeBackup(await downloadUnsavedCopy(repo, session.snapshot.project ?? project)));
    } catch (error) {
      const e = toStorageError(error, 'The backup could not be created.');
      setBackupNotice({ tone: 'error', title: `No backup was downloaded: ${e.message}`, details: e.details });
    } finally {
      setBackingUp(false);
    }
  };
  useAudioSync(audio, project);
  // Scene/character undo history lives as long as this open project (reset on switch).
  const [history] = useState(
    () =>
      new VisualHistory({
        get: () => session.snapshot.project,
        subscribe: (listener) => session.state.subscribe(listener),
        update: (mutate) => session.update(mutate),
      }),
  );
  useEffect(() => {
    history.attach();
    selection.clear();
    animation.clear();
    motion.clear();
    syncSelection.clear();
    // A playing session takes over playback: the Animation workspace's clock pauses.
    sync.setTakeover(() => animation.pause());
    return () => {
      history.detach();
      selection.clear();
      animation.clear();
      motion.clear();
      // Closing or switching projects stops session playback and releases audio.
      sync.unload();
      sync.setTakeover(null);
      // Analyses belong to the closed project: cancel running ones and release cached features.
      analysis.clear();
      syncSelection.clear();
    };
  }, [history, selection, animation, motion, sync, syncSelection, analysis]);
  // The session controller follows the project and the selected session (default: the first).
  const selectedSessionId = useObservableSelector(syncSelection, (s) => s.sessionId);
  const sessionId = project.avSessions.some((s) => s.id === selectedSessionId) ? selectedSessionId : (project.avSessions[0]?.id ?? null);
  useEffect(() => {
    sync.load(project, sessionId);
  }, [sync, project, sessionId]);

  const update = useCallback((mutate: (p: Project) => Project) => session.update(mutate), [session]);
  const value = useMemo<ProjectContextValue>(() => ({ project, session, update }), [project, session, update]);
  const goTab = useCallback(
    (next: WorkspaceTab) => navigate({ name: 'project', projectId: project.id, tab: next }),
    [navigate, project.id],
  );

  return (
    <ProjectContext.Provider value={value}>
      <VisualHistoryContext.Provider value={history}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="border-b border-zinc-800 bg-zinc-900/40 px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold" data-testid="project-title">
                {project.name}
              </h1>
            </div>
            <SaveIndicator status={state.status} error={state.saveError} onSave={() => void session.saveNow()} />
          </div>
          {session.editingLock === 'unprotected' && (
            <p className="mt-2 text-xs text-amber-200" data-testid="lock-unprotected">
              This browser can’t detect other tabs. Open this project in only one tab at a time, or one tab’s saves will
              overwrite the other’s.
            </p>
          )}
          {state.status === 'error' && state.saveError && (
            <div className="mt-3">
              <Alert
                title="Your latest changes are not saved yet."
                action={
                  <Button size="sm" onClick={() => void backup()} disabled={backingUp} data-testid="download-unsaved">
                    {backingUp ? 'Preparing backup…' : 'Download a backup'}
                  </Button>
                }
              >
                {state.saveError} They are kept while this project stays open. Download a backup file to keep them safe (import it from the Projects page).
              </Alert>
              {backupNotice && (
                <div className="mt-2" data-testid="backup-result" data-tone={backupNotice.tone}>
                  <Alert tone={backupNotice.tone} title={backupNotice.title}>
                    {backupNotice.details.map((d) => <p key={d}>{d}</p>)}
                  </Alert>
                </div>
              )}
            </div>
          )}
          <nav aria-label="Workspaces" className="mt-3 -mb-3 flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <a
                key={t}
                href={`#/project/${project.id}/${t}`}
                aria-current={t === tab ? 'page' : undefined}
                className={`rounded-t-md border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap ${
                  t === tab
                    ? 'border-accent-400 text-zinc-50'
                    : 'border-transparent text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
                }`}
              >
                {TAB_LABELS[t]}
              </a>
            ))}
          </nav>
        </div>
        <div className="grid flex-1 gap-4 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <section aria-label={`${TAB_LABELS[tab]} workspace`} className="min-w-0">
            <ErrorBoundary key={tab} label={`${TAB_LABELS[tab]} workspace`}>
              {tab === 'music' && <MusicWorkspace />}
              {tab === 'animation' && <AnimationWorkspace />}
              {tab === 'sync' && <SyncWorkspace />}
              {tab === 'visualizer' && <VisualizerWorkspace />}
              {tab === 'audio' && <AudioWorkspace />}
              {tab === 'assistant' && <AssistantWorkspace onOpen={goTab} />}
              {tab === 'settings' && <SettingsWorkspace onDeleted={() => navigate({ name: 'dashboard' })} />}
            </ErrorBoundary>
          </section>
          <ErrorBoundary label="Prompt panel">
            <PromptPanel navigate={goTab} />
          </ErrorBoundary>
        </div>
      </div>
    </VisualHistoryContext.Provider>
    </ProjectContext.Provider>
  );
}
